import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import { formatCreditBalance } from '@/lib/formatCredits';
import { referralProgress, referralText } from '@/lib/referralSharing';

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: 16, padding: 20, gap: 12 },
  row: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  track: { height: 6, borderRadius: 3, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 3 },
});

export default function ReferralProgress({ stats }) {
  const { theme } = useTheme();
  const dark = isDarkTheme(theme);
  const ink = dark ? '#F5F5F7' : '#1D1D1F';
  const secondary = dark ? '#A1A1A6' : '#6E6E73';
  const line = dark ? '#38383B' : '#E5E5E7';
  const { t } = useTranslation();
  const progress = referralProgress(stats);
  if (!progress.reward && !progress.atCap) return null;
  return (
    <View style={[styles.card, { borderColor: line }]}>
      <View style={styles.row}>
        <Text size="s" weight="semibold" style={{ color: ink }} accessibilityRole="header" accessibilityLevel={3}>
          {t(progress.atCap ? 'settings.referral.limitReached' : 'settings.referral.nextGoal')}
        </Text>
        <Text size="xs" style={{ color: secondary }}>
          {referralText(t, 'goalCount', { COUNT: progress.completed, GOAL: progress.nextGoal })}
        </Text>
      </View>
      {progress.nextGoal > 0 && (
        <View accessibilityRole="progressbar" accessibilityLabel={t('settings.referral.nextGoal')}
          accessibilityValue={{ min: 0, max: progress.nextGoal, now: Math.min(progress.completed, progress.nextGoal) }}
          aria-valuemin={0} aria-valuemax={progress.nextGoal} aria-valuenow={Math.min(progress.completed, progress.nextGoal)}
          style={[styles.track, { backgroundColor: line }]}>
          <View style={[styles.fill, { width: `${Math.min(100, progress.completed / progress.nextGoal * 100)}%`, backgroundColor: ink }]} />
        </View>
      )}
      <Text size="s" style={{ color: secondary, lineHeight: 20 }}>
        {progress.atCap
          ? t('settings.referral.limitReachedDescription')
          : referralText(t, progress.remaining === 1 ? 'goalOne' : 'goalMany', {
            COUNT: progress.remaining, CREDITS: formatCreditBalance(progress.nextCredits),
          })}
      </Text>
      {!progress.atCap && <Text size="xs" style={{ color: secondary }}>
        {progress.nextBonus > 0 ? referralText(t, 'goalBonusExplanation', {
          BONUS: formatCreditBalance(progress.nextBonus), CREDITS: formatCreditBalance(progress.reward),
        }) : t('settings.referral.goalExplanation')}
      </Text>}
    </View>
  );
}
